/**
 * Banner + Inline Product Carousel
 *
 * Desktop/tablet: arrow buttons scroll the viewport by one card (smooth).
 * Nav buttons match circular-items-row__nav: RTL-aware disabled edges, ResizeObserver, cleanup on section unload.
 * Mobile: rail is hidden; section uses vertical 2-col grid + "View more" CTA.
 * @module banner-inline-carousel
 */

/**
 * @param {HTMLElement} viewport
 * @param {HTMLButtonElement | null | undefined} prevBtn
 * @param {HTMLButtonElement | null | undefined} nextBtn
 */
function updateBannerInlineNavDisabled(viewport, prevBtn, nextBtn) {
  if (!viewport || (!prevBtn && !nextBtn)) return;
  const tol = 3;
  const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
  if (maxScroll <= tol) {
    if (prevBtn) prevBtn.disabled = true;
    if (nextBtn) nextBtn.disabled = true;
    return;
  }
  const dir = getComputedStyle(viewport).direction;
  const sl = viewport.scrollLeft;
  let atStart;
  let atEnd;
  if (dir === 'rtl') {
    atStart = sl >= maxScroll - tol;
    atEnd = sl <= tol;
  } else {
    atStart = sl <= tol;
    atEnd = sl >= maxScroll - tol;
  }
  if (prevBtn) prevBtn.disabled = atStart;
  if (nextBtn) nextBtn.disabled = atEnd;
}

/**
 * Initializes carousel behavior for a single section: scroll-by-card and prev/next disable at bounds.
 * @param {HTMLElement} [section] - Section element with class `.banner-inline-carousel`
 * @returns {void}
 */
function initBannerInlineCarousel(section) {
  if (!section || section.dataset.bannerInlineCarouselInitialized === 'true') return;

  const viewport = section.querySelector('[data-banner-inline-viewport]');
  const rail = section.querySelector('[data-banner-inline-rail]');
  if (!viewport || !rail) return;

  const prevBtn = section.querySelector('[data-banner-inline-nav="prev"]');
  const nextBtn = section.querySelector('[data-banner-inline-nav="next"]');

  /**
   * @returns {number} Scroll step (one card width + gap)
   */
  const getStep = () => {
    const item = rail.querySelector('.banner-inline-carousel__rail-item');
    if (!item) return Math.max(240, viewport.clientWidth * 0.6);

    const rect = item.getBoundingClientRect();
    const styles = window.getComputedStyle(rail);
    const gap = parseFloat(styles.gap || '0') || 0;
    return rect.width + gap;
  };

  /**
   * @param {number} dir - -1 for prev, 1 for next
   */
  const scrollByStep = (dir) => {
    const step = getStep();
    viewport.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  const handlePrev = (e) => {
    if (prevBtn?.disabled) return;
    e.preventDefault();
    scrollByStep(-1);
  };
  const handleNext = (e) => {
    if (nextBtn?.disabled) return;
    e.preventDefault();
    scrollByStep(1);
  };

  prevBtn?.addEventListener('click', handlePrev);
  nextBtn?.addEventListener('click', handleNext);

  let scrollRaf = 0;
  const onScroll = () => {
    if (scrollRaf) return;
    scrollRaf = requestAnimationFrame(() => {
      scrollRaf = 0;
      updateBannerInlineNavDisabled(viewport, prevBtn, nextBtn);
    });
  };

  viewport.addEventListener('scroll', onScroll, { passive: true });

  let layoutRaf = 0;
  const onLayout = () => {
    if (layoutRaf) return;
    layoutRaf = requestAnimationFrame(() => {
      layoutRaf = 0;
      updateBannerInlineNavDisabled(viewport, prevBtn, nextBtn);
    });
  };

  /** @type {ResizeObserver | undefined} */
  let ro;
  if (typeof ResizeObserver !== 'undefined') {
    ro = new ResizeObserver(onLayout);
    ro.observe(viewport);
  }

  const onUnload = (/** @type {Event} */ e) => {
    const t = e.target;
    if (!(t instanceof Element) || !t.contains(section)) return;
    viewport.removeEventListener('scroll', onScroll);
    document.removeEventListener('shopify:section:unload', onUnload);
    ro?.disconnect();
    prevBtn?.removeEventListener('click', handlePrev);
    nextBtn?.removeEventListener('click', handleNext);
    delete section.dataset.bannerInlineCarouselInitialized;
  };
  document.addEventListener('shopify:section:unload', onUnload);

  updateBannerInlineNavDisabled(viewport, prevBtn, nextBtn);
  requestAnimationFrame(() => {
    updateBannerInlineNavDisabled(viewport, prevBtn, nextBtn);
  });

  section.dataset.bannerInlineCarouselInitialized = 'true';
}

/**
 * Initializes all banner-inline-carousel sections under a root.
 * @param {Document|HTMLElement} [root=document] - Root to query
 * @returns {void}
 */
function initAllBannerInlineCarousels(root = document) {
  root.querySelectorAll?.('.banner-inline-carousel')?.forEach(initBannerInlineCarousel);
}

// Bind so carousel works when section is in DOM (including low-priority / deferred load).
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAllBannerInlineCarousels());
} else {
  initAllBannerInlineCarousels();
}
window.addEventListener('load', () => initAllBannerInlineCarousels());

// Shopify Theme Editor / hot reload support
document.addEventListener('shopify:section:load', (event) => {
  if (!event?.target) return;
  initAllBannerInlineCarousels(event.target);
});
